import { Injectable, inject } from '@angular/core';
import { combineLatest, map, Observable, of } from 'rxjs';
import { RolloutProgressReportEntities } from '../../shared/self-inspection-reports/service/entities';
import { RolloutProgressReportsService } from '../../shared/self-inspection-reports/service/reports/rollout-progress-reports.service';
import { DistributionPointProperties } from '../span-progress-entities';

@Injectable({ providedIn: 'root' })
export class KpReportStatusService {
  private readonly reportService = inject(RolloutProgressReportsService);

  getPointStatuses(
    ifsNumber: string,
    point: DistributionPointProperties,
  ): Observable<Record<string, RolloutProgressReportEntities.v3.ReportStatus>> {
    return combineLatest(
      this.getKpReferences(point).map((kp) =>
        this.getKpReportStatus(ifsNumber, kp),
      ),
    ).pipe(map((statuses) => Object.fromEntries(statuses)));
  }

  linkedHpReportsDone(point: DistributionPointProperties): boolean {
    const hpReports = this.reportService.storedV3Reports().HP;

    return this.getKpReferences(point).every((kp) => {
      const hpId = this.getLinkedHpId(kp);

      return (
        hpId != null &&
        hpReports?.[hpId]?.status ===
          RolloutProgressReportEntities.v3.ReportStatus.DONE
      );
    });
  }

  getLinkedHpId(point: DistributionPointProperties): string | undefined {
    const [linkedFtthMarker] = point.linkedFtthMarkers ?? [];
    return linkedFtthMarker?.leadId ?? linkedFtthMarker?.sourceKey;
  }

  private getKpReferences(
    point: DistributionPointProperties,
  ): DistributionPointProperties[] {
    return point.aggregatedMarkerReferences ?? [point];
  }

  private getKpReportStatus(
    ifsNumber: string,
    kp: DistributionPointProperties,
  ): Observable<
    readonly [string, RolloutProgressReportEntities.v3.ReportStatus]
  > {
    const hpId = this.getLinkedHpId(kp);
    if (!hpId) {
      return of([
        kp.id,
        RolloutProgressReportEntities.v3.ReportStatus.UNKNOWN,
      ] as const);
    }

    return this.reportService
      .getReportStatus$(
        ifsNumber,
        RolloutProgressReportEntities.v3.MarkerTypes.HP,
        hpId,
      )
      .pipe(map((status) => [kp.id, status] as const));
  }
}
